import { setTelemetrySession } from './lib/telemetry';
import { getLocalDb, liveQuery } from '@flm9/sync';
import { queueRoundOp, queuedConfirmations, startRefereeSync } from './lib/sync';
import { useCallback, useEffect, useState } from 'react';
import { ScoreScreen } from './screens/Score';
import { SessionPicker } from './screens/SessionPicker';
import { RosterScreen } from './screens/RosterScreen';
import { DrillPicker } from './screens/DrillPicker';
import { CustomDrillScreen, type CustomConfig } from './screens/CustomDrillScreen';
import { ScenarioPicker } from './screens/ScenarioPicker';
import { ModePicker } from './screens/ModePicker';
import { DistancePicker } from './screens/DistancePicker';
import { ParPicker } from './screens/ParPicker';
import { PlannedDisciplinePicker } from './screens/PlannedDisciplinePicker';
import { RoundCompleteScreen } from './screens/RoundCompleteScreen';
import { OrderPicker } from './screens/OrderPicker';
import { TimerDebug } from './screens/TimerDebug';
import { BugReportsScreen } from './screens/BugReportsScreen';
import { BugButton } from './components/BugButton';
import { drainBugReports, pendingBugReports, startBugReportSync } from './lib/bugReports';
import { BadeschlussSignatures } from './components/BadeschlussSignatures';
import { TimerProvider, useTimer } from './lib/timer/provider';
import {
  bindInstallPrompt,
  checkStorage,
  isStandalone,
  requestPersist,
  subscribeInstallAvailable,
  triggerInstallPrompt,
  type StorageStatus,
} from './lib/storage';
import {
  api,
  type LastStageConfig,
  applyCallResult,
  isOffline,
  type LiveAssignment,
  type PlannedStageInfo,
  type RangeSession,
  type DrillRow,
  type ScoringMode,
} from './lib/api';
import { errorText } from './lib/errorText';
import { currentGenderStyle, useTerm } from './lib/wording';

type InstallBanner =
  | { kind: 'idle' }
  | { kind: 'prompt-available' }
  | { kind: 'manual-ios' }
  | { kind: 'busy' }
  | { kind: 'persisted' }
  | { kind: 'persist-denied' }
  | { kind: 'persist-unsupported' };

type View =
  | { kind: 'pick-session' }
  | { kind: 'roster'; session: RangeSession }
  | {
      kind: 'pick-drill';
      session: RangeSession;
      /**
       * When set, the next picked library drill rewrites this
       * planned stage instead of creating a new ad-hoc one. The
       * referee still confirms mode/distance afterwards.
       */
      replaceForStageId?: string;
    }
  | { kind: 'pick-scenario'; session: RangeSession }
  | {
      kind: 'pick-custom';
      session: RangeSession;
      /** Set on "Vorheriges wiederholen" — pre-fills the builder. */
      prefill: LastStageConfig | null;
    }
  | {
      kind: 'pick-mode';
      session: RangeSession;
      drill: DrillRow;
      replaceForStageId?: string;
      /** Set when this round is a custom one-off (no catalog drill). */
      custom?: CustomPayload;
    }
  | {
      kind: 'pick-distance';
      session: RangeSession;
      drill: DrillRow;
      mode: ScoringMode;
      replaceForStageId?: string;
      custom?: CustomPayload;
    }
  | {
      kind: 'pick-par';
      session: RangeSession;
      drill: DrillRow;
      mode: ScoringMode;
      distanceCm: number;
      replaceForStageId?: string;
      custom?: CustomPayload;
    }
  | {
      kind: 'pick-planned-discipline';
      session: RangeSession;
      stage: PlannedStageInfo['stage'];
    }
  | { kind: 'pick-order'; session: RangeSession; stageId: string; stageName: string }
  | {
      kind: 'score';
      session: RangeSession;
      active: LiveAssignment;
      roster: LiveAssignment[];
      /**
       * Set when "Aufrufen" found a different shooter up next than the
       * button showed (REF-19): that shooter is shown on the button instead
       * of being called, and the next tap calls them.
       */
      nextUp?: LiveAssignment;
    }
  | {
      kind: 'round-complete';
      session: RangeSession;
      stageId: string | null;
      stageName: string;
    }
  | { kind: 'timer-debug'; from: View }
  | { kind: 'bug-reports' };

/** Carried through the mode/distance/par steps for a custom one-off drill. */
type CustomPayload = CustomConfig;

/**
 * Build an in-memory `DrillRow` for a custom drill so it flows through the
 * existing Mode / Distance / PAR pickers unchanged (they read only name,
 * shotCount, distanceCm, parSeconds, parMode and targetName). `drill.id` is
 * an empty sentinel — it is never sent to the server; the final commit keys
 * off `view.custom` and calls `startCustomRound`. When repeating a previous
 * stage, the prefill seeds the distance + PAR defaults.
 */
function syntheticDrillRow(custom: CustomPayload, prefill: LastStageConfig | null): DrillRow {
  return {
    drill: {
      id: '',
      name: custom.name,
      targetTypeId: custom.targetTypeId,
      description: null,
      distanceCm: prefill?.distanceCm ?? null,
      shotCount: custom.shotCount,
      magazines: custom.magazines,
      timeLimitS: null,
      locationId: null,
      parSeconds: prefill?.effectiveParSeconds ?? null,
      parMode: prefill?.effectiveParMode ?? 'soft',
      defaultDisciplineId: prefill?.disciplineId ?? null,
    },
    schemaCode: '',
    schemaKind: 'ring_value',
    schemaName: '',
    targetName: custom.targetName,
    targetImageBlobId: null,
  };
}

/**
 * Reconstruct the custom-builder pre-fill from the in-flight payload + its
 * synthetic drill, so stepping "back" into the builder keeps the referee's
 * target / shot / distance / PAR choices instead of resetting them.
 */
function payloadToPrefill(custom: CustomPayload, drill: DrillRow): LastStageConfig {
  return {
    name: custom.name,
    targetTypeId: custom.targetTypeId,
    targetName: custom.targetName,
    shotCount: custom.shotCount,
    magazines: custom.magazines,
    scoringMode: 'precision',
    distanceCm: drill.drill.distanceCm ?? 0,
    disciplineId: drill.drill.defaultDisciplineId,
    effectiveParSeconds: drill.drill.parSeconds,
    effectiveParMode: drill.drill.parMode,
  };
}

const isIos = (): boolean =>
  typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent);

function nextPending(roster: LiveAssignment[], active: LiveAssignment): LiveAssignment | null {
  return (
    roster.find(
      (a) =>
        a.stage.id === active.stage.id &&
        a.round.roundNo === active.round.roundNo &&
        a.assignment.id !== active.assignment.id &&
        a.assignment.completedAt == null &&
        a.assignment.disqualifiedAt == null &&
        // A no-show is never next (same rule as the board / native app).
        !a.participationInactive &&
        a.shotCount < a.stage.shotCount,
    ) ?? null
  );
}

function shooterLabelOf(a: LiveAssignment): string {
  return `${a.memberLastName ?? 'Gast'}, ${a.memberFirstName ?? ''}`.trim().replace(/, $/, '');
}

/**
 * Wrapper around ScenarioPicker that drives the expand-scenario-onto-session
 * mutation. Lives in App.tsx because it needs to bounce back to the
 * roster view on success / cancel; keeping the mutation here also
 * means `ScenarioPicker` itself stays presentation-only.
 */
function ScenarioExpansionPicker({
  session,
  onCancel,
  onDone,
}: {
  session: RangeSession;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      {error && <p className="rounded bg-red-900/30 p-3 text-sm text-red-300">{error}</p>}
      <ScenarioPicker
        onCancel={onCancel}
        locationId={session.locationId ?? null}
        picking={busy}
        onPicked={async (scenarioId) => {
          setBusy(true);
          setError(null);
          try {
            await api.expandScenarioToSession(session.id, scenarioId);
            onDone();
          } catch (err) {
            setError(errorText(err));
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

function TimerChip({ onClick }: { onClick: () => void }) {
  const { status, isConnected, bleSupported, pairing } = useTimer();
  const color = !bleSupported
    ? 'bg-zinc-600'
    : status.reconnecting
      ? 'bg-amber-400'
      : status.disconnected || !isConnected
        ? 'bg-red-500'
        : status.state === 'running'
          ? 'bg-emerald-500'
          : status.state === 'armed' || pairing
            ? 'bg-amber-400'
            : 'bg-emerald-500';
  const label = !bleSupported
    ? 'Timer (nicht verfügbar)'
    : pairing
      ? 'Timer koppeln…'
      : status.reconnecting
        ? 'Timer verbindet…'
        : isConnected
          ? (status.deviceName ?? 'Timer')
          : 'Timer';
  return (
    <button
      type="button"
      onClick={onClick}
      title="SG Timer-Status"
      className="flex min-h-12 items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 hover:border-zinc-500"
    >
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      <span>{label}</span>
    </button>
  );
}

export function App() {
  return (
    <TimerProvider>
      <AppInner />
    </TimerProvider>
  );
}

function AppInner() {
  useEffect(() => startRefereeSync(), []);
  useEffect(() => startBugReportSync(), []);
  const t = useTerm();
  const [view, setView] = useState<View>({ kind: 'pick-session' });
  // "Fehler merken": open (undescribed) presses → banner on the session list.
  const [openBugs, setOpenBugs] = useState(0);
  const refreshOpenBugs = useCallback(() => {
    api
      .myBugReports()
      .then((rows) => setOpenBugs(rows.length + pendingBugReports()))
      .catch(() => setOpenBugs(pendingBugReports()));
  }, []);
  useEffect(() => {
    if (view.kind === 'pick-session') refreshOpenBugs();
  }, [view.kind, refreshOpenBugs]);
  // The list loaded (also right after signing in): send presses queued while
  // signed out, then refresh the banner.
  const onSessionsLoaded = useCallback(() => {
    void drainBugReports().finally(refreshOpenBugs);
  }, [refreshOpenBugs]);
  // Debug flight recorder follows the open training (live ones only).
  useEffect(() => {
    setTelemetrySession('session' in view ? view.session.id : null);
  }, [view]);
  const [online, setOnline] = useState(navigator.onLine);
  // Changes still waiting in the offline queue (shots, confirms, calls).
  const [queuedCount, setQueuedCount] = useState(0);
  useEffect(() => {
    const sub = liveQuery(() =>
      getLocalDb().outbox.where('status').anyOf('pending', 'inflight').count(),
    ).subscribe({ next: setQueuedCount, error: (err) => console.error('queue count failed', err) });
    return () => sub.unsubscribe();
  }, []);
  const [storage, setStorage] = useState<StorageStatus | null>(null);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [banner, setBanner] = useState<InstallBanner>({ kind: 'idle' });
  // Session pending a Badeschluss — drives the two-signature overlay.
  const [badeschlussFor, setBadeschlussFor] = useState<RangeSession | null>(null);
  // Discipline catalogue (pistol / pistol_carbine / rifle) — loaded once,
  // fed to the per-stage discipline picker in ParPicker.
  const [disciplines, setDisciplines] = useState<Array<{ id: string; code: string }>>([]);
  const [disciplinesError, setDisciplinesError] = useState<string | null>(null);

  // The discipline catalogue is loaded once at mount, but the referee may not
  // be authenticated yet (sign-in happens inside SessionPicker). A pre-login
  // 401 would otherwise leave the list permanently empty — and an empty list
  // means the per-stage discipline picker can never be shown, which silently
  // disables the "Starten" button on the PAR step. So expose a re-runnable
  // loader and re-fetch whenever we enter a discipline-picking view while the
  // list is still empty (by then the user is signed in).
  const loadDisciplines = useCallback(() => {
    setDisciplinesError(null);
    void api
      .disciplines()
      .then((d) => setDisciplines(d.map((x) => ({ id: x.id, code: x.code }))))
      .catch((err: unknown) => setDisciplinesError(errorText(err)));
  }, []);

  useEffect(() => {
    loadDisciplines();
  }, [loadDisciplines]);

  // Recover from a pre-login load failure: if we reach a view that needs the
  // discipline catalogue but still have none, try again now that we're authed.
  useEffect(() => {
    if (
      disciplines.length === 0 &&
      (view.kind === 'pick-par' || view.kind === 'pick-planned-discipline')
    ) {
      loadDisciplines();
    }
  }, [view.kind, disciplines.length, loadDisciplines]);

  useEffect(() => {
    const onOnline = () => setOnline(navigator.onLine);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOnline);
    void checkStorage().then(setStorage);

    const detachBind = bindInstallPrompt();
    const detachSub = subscribeInstallAvailable(setInstallAvailable);
    const onInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOnline);
      detachBind();
      detachSub();
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (
      banner.kind === 'persisted' ||
      banner.kind === 'persist-denied' ||
      banner.kind === 'persist-unsupported'
    ) {
      const t = window.setTimeout(() => setBanner({ kind: 'idle' }), 4000);
      return () => window.clearTimeout(t);
    }
    return;
  }, [banner.kind]);

  const showInstallStrip = !installed && !storage?.persisted;

  async function onInstallClick() {
    setBanner({ kind: 'busy' });
    if (installAvailable) {
      const result = await triggerInstallPrompt();
      if (result.outcome === 'accepted') setInstalled(true);
    } else if (isIos()) {
      setBanner({ kind: 'manual-ios' });
      return;
    }
    const persistResult = await requestPersist();
    if (persistResult.ok) {
      setBanner({ kind: 'persisted' });
      setStorage((s) => (s ? { ...s, persisted: true } : s));
    } else if (persistResult.reason === 'unsupported') {
      setBanner({ kind: 'persist-unsupported' });
    } else {
      setBanner({ kind: 'persist-denied' });
    }
  }

  // "Aufrufen" — call (= claim) the next pending shooter on the same
  // stage+round and open their Score screen. Decoupled from Bestätigen:
  // the confirmed shooter stays on screen until the referee calls the
  // next one. Re-fetches the roster so shotCounts are fresh. If there's
  // no pending shooter left in the round, we kick over to the
  // round-complete view so the referee can pick what's next instead of
  // dropping into the roster.
  async function gotoNext(current: View & { kind: 'score' }, shown: LiveAssignment | null) {
    try {
      // Offline (REF-3): fall back to the last roster. The current shooter is
      // confirmed (Aufrufen only shows after Bestätigen), and shooters whose
      // confirmation is still queued count as done either way.
      let offline = false;
      let fresh: LiveAssignment[];
      try {
        fresh = await api.liveAssignments(current.session.id);
      } catch (err) {
        if (!isOffline(err)) throw err;
        offline = true;
        fresh = current.roster;
      }
      const confirmed = await queuedConfirmations();
      confirmed.add(current.active.assignment.id);
      const now = new Date().toISOString();
      fresh = fresh.map((a) =>
        confirmed.has(a.assignment.id) && a.assignment.completedAt == null
          ? { ...a, assignment: { ...a.assignment, completedAt: now } }
          : a,
      );
      // Offline the server can't settle a conflict before scoring starts —
      // skip shooters the last roster shows held by another referee, and
      // partly scored ones whose shots aren't cached here (new shots would
      // restart at #1 and collide with the server's).
      let pool = fresh;
      if (offline) {
        const db = getLocalDb();
        const usable = await Promise.all(
          fresh.map(
            async (a) =>
              (a.ownedByMe || a.assignment.assignedRefereeUserId == null) &&
              (a.shotCount === 0 ||
                (await db.shots.where('roundAssignmentId').equals(a.assignment.id).count()) >=
                  a.shotCount),
          ),
        );
        pool = fresh.filter((_, i) => usable[i]);
      }
      const candidate = nextPending(pool, current.active);
      if (!candidate && offline && nextPending(fresh, current.active)) {
        // Shooters are still waiting, just not callable without the server.
        alert(
          currentGenderStyle() === 'masculine'
            ? 'Offline — die übrigen Schützen sind anderen Standführern zugeordnet oder haben Schüsse, die dieses Tablet nicht kennt. Aufrufen geht wieder mit Verbindung.'
            : 'Offline — die übrigen Schützen sind anderen Standführer:innen zugeordnet oder haben Schüsse, die dieses Tablet nicht kennt. Aufrufen geht wieder mit Verbindung.',
        );
        return;
      }
      if (candidate && shown && candidate.assignment.id !== shown.assignment.id) {
        // REF-19: the order changed since the button was drawn (another
        // tablet called someone, or a shuffle). Don't call someone the
        // Standführer:in didn't pick — show who is up now; the next tap
        // calls them.
        setView({ ...current, roster: fresh, nextUp: candidate });
        return;
      }
      if (!candidate) {
        setView({
          kind: 'round-complete',
          session: current.session,
          stageId: current.active.stage.id,
          stageName: current.active.stage.name,
        });
        return;
      }
      let active: LiveAssignment | null = null;
      if (!offline) {
        try {
          active = applyCallResult(candidate, await api.callShooter(candidate.assignment.id));
        } catch (err) {
          if (!isOffline(err)) throw err;
        }
      }
      if (!active) {
        // Queue the call ahead of this shooter's shots — the server only
        // accepts shots from the referee holding the call. Same lane as the
        // shooter before (the server can't be asked for a lane offline).
        // Unknown lane → leave it to the server (kept / participation lane);
        // an explicit null would clear it and fail with lane_required.
        const laneId = current.active.laneId ?? undefined;
        await queueRoundOp('round.call', {
          roundAssignmentId: candidate.assignment.id,
          ...(laneId ? { laneId } : {}),
        });
        active = applyCallResult(
          laneId ? { ...candidate, laneId, laneName: current.active.laneName } : candidate,
          { lockedAt: now, calledAt: now },
        );
        active = { ...active, ownedByMe: true };
      }
      setView({ kind: 'score', session: current.session, active, roster: fresh });
    } catch (err) {
      console.error('gotoNext failed', err);
      alert(`Aufrufen fehlgeschlagen: ${errorText(err)}`);
      setView({ kind: 'roster', session: current.session });
    }
  }

  // Call (= claim) and start the first pending shooter on the given stage+round.
  // Used after instantiating a new drill or hitting "Nochmal".
  async function startFirstAssignmentOnStage(
    session: RangeSession,
    stageId: string,
    shuffle = false,
  ) {
    let fresh = await api.liveAssignments(session.id);
    if (shuffle) {
      const newest = fresh
        .filter((a) => a.stage.id === stageId)
        .reduce<LiveAssignment | null>(
          (best, a) => (best == null || a.round.roundNo > best.round.roundNo ? a : best),
          null,
        );
      if (newest) {
        await api.shuffleRoundOrder(newest.round.id);
        fresh = await api.liveAssignments(session.id);
      }
    }
    // Pick highest round number for this stage that has any pending
    // assignment — the just-created round.
    const stageAssignments = fresh
      .filter((a) => a.stage.id === stageId)
      .sort((a, b) => b.round.roundNo - a.round.roundNo);
    const lastRoundNo = stageAssignments[0]?.round.roundNo;
    const candidate = stageAssignments.find(
      (a) =>
        a.round.roundNo === lastRoundNo &&
        a.assignment.completedAt == null &&
        a.shotCount < a.stage.shotCount,
    );
    if (!candidate) {
      setView({ kind: 'roster', session });
      return;
    }
    const res = await api.callShooter(candidate.assignment.id);
    setView({ kind: 'score', session, active: applyCallResult(candidate, res), roster: fresh });
  }

  // Badeschluss now requires two on-tablet signatures — open the
  // signature overlay instead of closing the session directly. The
  // overlay performs sessions.close once both signatures are captured.
  function badeschluss(session: RangeSession) {
    setBadeschlussFor(session);
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <header className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="SV flm9" className="h-7 w-auto" />
          <h1 className="text-lg font-bold">Standführer-App</h1>
        </div>
        <div className="flex items-center gap-2">
          {'session' in view && (
            <BugButton
              sessionId={view.session.id}
              screen={view.kind}
              context={view.kind === 'score' ? { assignmentId: view.active.assignment.id } : {}}
            />
          )}
          <TimerChip
            onClick={() =>
              setView((v) => (v.kind === 'timer-debug' ? v : { kind: 'timer-debug', from: v }))
            }
          />
          <span
            role="status"
            className={
              online
                ? 'text-xs text-emerald-400'
                : 'rounded-full border border-amber-600 px-2 py-0.5 text-xs font-semibold text-amber-300'
            }
          >
            {online ? 'online' : 'offline'}
            {queuedCount > 0 && ` · ${queuedCount} ausstehend`}
          </span>
        </div>
      </header>

      {storage?.lowSpace && (
        <div className="bg-amber-900/40 px-4 py-2 text-xs text-amber-300">
          ⚠ Wenig freier Speicher ({storage.estimateMB} MB). Bitte Speicher freigeben.
        </div>
      )}

      {showInstallStrip && banner.kind !== 'manual-ios' && (
        <button
          onClick={() => void onInstallClick()}
          disabled={banner.kind === 'busy'}
          className="block w-full bg-emerald-900/40 px-4 py-2 text-left text-xs text-emerald-200 hover:bg-emerald-800/40 disabled:opacity-50"
        >
          {banner.kind === 'busy'
            ? 'Bitte Eingabeaufforderung im Browser bestätigen…'
            : installAvailable
              ? 'App installieren + persistenten Speicher anfordern →'
              : 'Persistenten Speicher anfordern →'}
        </button>
      )}

      {banner.kind === 'manual-ios' && (
        <div className="bg-emerald-900/40 px-4 py-2 text-xs text-emerald-200">
          Auf iOS: Teilen-Symbol → „Zum Home-Bildschirm" → erneut öffnen.
        </div>
      )}
      {banner.kind === 'persisted' && (
        <div className="bg-emerald-700/40 px-4 py-2 text-xs text-emerald-100">
          ✓ Persistenter Speicher gewährt — Schussdaten überleben Browser-Neustarts.
        </div>
      )}
      {banner.kind === 'persist-denied' && (
        <div className="bg-amber-900/40 px-4 py-2 text-xs text-amber-200">
          Browser hat den Antrag abgelehnt. Tipp: App zur Startseite hinzufügen, dann erneut
          versuchen.
        </div>
      )}
      {banner.kind === 'persist-unsupported' && (
        <div className="bg-zinc-800/60 px-4 py-2 text-xs text-zinc-300">
          Dieser Browser kennt die StorageManager-API nicht. Daten können bei Speichermangel
          gelöscht werden.
        </div>
      )}

      <main className="p-4">
        {view.kind === 'pick-session' && openBugs > 0 && (
          <button
            type="button"
            onClick={() => setView({ kind: 'bug-reports' })}
            className="mb-4 block w-full rounded-lg border border-amber-700 bg-amber-900/30 px-4 py-3 text-left text-sm text-amber-100"
          >
            🐞 Du hast {openBugs} Fehler gemerkt — jetzt kurz beschreiben →
          </button>
        )}
        {view.kind === 'pick-session' && (
          <SessionPicker
            onPick={(session, roster) => setView({ kind: 'roster', session })}
            onSessionsLoaded={onSessionsLoaded}
          />
        )}
        {view.kind === 'bug-reports' && (
          <BugReportsScreen onBack={() => setView({ kind: 'pick-session' })} />
        )}

        {view.kind === 'roster' && (
          <RosterScreen
            session={view.session}
            onPickSession={() => setView({ kind: 'pick-session' })}
            onPickDrill={() => setView({ kind: 'pick-drill', session: view.session })}
            onPickScenario={() => setView({ kind: 'pick-scenario', session: view.session })}
            onBadeschluss={() => void badeschluss(view.session)}
            onScore={(active, roster) =>
              setView({ kind: 'score', session: view.session, active, roster })
            }
            online={online}
            onSessionChange={(session) =>
              // Only while this roster is still shown (the reload is async).
              setView((v) =>
                v.kind === 'roster' && v.session.id === session.id ? { ...v, session } : v,
              )
            }
          />
        )}

        {view.kind === 'pick-scenario' && (
          <ScenarioExpansionPicker
            session={view.session}
            onCancel={() => setView({ kind: 'roster', session: view.session })}
            onDone={() => setView({ kind: 'roster', session: view.session })}
          />
        )}

        {view.kind === 'pick-drill' && (
          <DrillPicker
            session={view.session}
            replaceForStageId={view.replaceForStageId}
            onPicked={(drill) =>
              setView({
                kind: 'pick-mode',
                session: view.session,
                drill,
                ...(view.replaceForStageId ? { replaceForStageId: view.replaceForStageId } : {}),
              })
            }
            onStartPlanned={(stage) => {
              // Always confirm the discipline before activating a planned
              // stage, so every shot stage carries one.
              setView({ kind: 'pick-planned-discipline', session: view.session, stage });
            }}
            onSkipPlanned={async (stageId) => {
              try {
                // The picker reloads the next planned stage (if any) into
                // the pinned card after this resolves.
                await api.skipPlannedStage(stageId);
              } catch (err) {
                console.error('skipPlannedStage failed', err);
                alert(err instanceof Error ? err.message : 'Überspringen fehlgeschlagen.');
              }
            }}
            onReplacePlanned={(stageId) =>
              setView({
                kind: 'pick-drill',
                session: view.session,
                replaceForStageId: stageId,
              })
            }
            onCustom={() => setView({ kind: 'pick-custom', session: view.session, prefill: null })}
            onRepeatPrevious={(cfg) =>
              setView({ kind: 'pick-custom', session: view.session, prefill: cfg })
            }
            onCancel={() => setView({ kind: 'roster', session: view.session })}
          />
        )}

        {view.kind === 'pick-custom' && (
          <CustomDrillScreen
            session={view.session}
            prefill={view.prefill}
            onConfirm={(custom) =>
              setView({
                kind: 'pick-mode',
                session: view.session,
                drill: syntheticDrillRow(custom, view.prefill),
                custom,
              })
            }
            onBack={() => setView({ kind: 'pick-drill', session: view.session })}
          />
        )}

        {view.kind === 'pick-mode' && (
          <ModePicker
            drill={view.drill}
            onPicked={(mode) =>
              setView({
                kind: 'pick-distance',
                session: view.session,
                drill: view.drill,
                mode,
                ...(view.replaceForStageId ? { replaceForStageId: view.replaceForStageId } : {}),
                ...(view.custom ? { custom: view.custom } : {}),
              })
            }
            onBack={() =>
              view.custom
                ? setView({
                    kind: 'pick-custom',
                    session: view.session,
                    prefill: payloadToPrefill(view.custom, view.drill),
                  })
                : setView({
                    kind: 'pick-drill',
                    session: view.session,
                    ...(view.replaceForStageId
                      ? { replaceForStageId: view.replaceForStageId }
                      : {}),
                  })
            }
          />
        )}

        {view.kind === 'pick-distance' && (
          <DistancePicker
            drill={view.drill}
            mode={view.mode}
            onConfirm={async (distanceCm) => {
              // Distance is confirmed — advance to the PAR picker so
              // the referee can review / override the recommendation
              // (or skip with "Kein PAR") before the round is created.
              setView({
                kind: 'pick-par',
                session: view.session,
                drill: view.drill,
                mode: view.mode,
                distanceCm,
                ...(view.replaceForStageId ? { replaceForStageId: view.replaceForStageId } : {}),
                ...(view.custom ? { custom: view.custom } : {}),
              });
            }}
            onBack={() =>
              setView({
                kind: 'pick-mode',
                session: view.session,
                drill: view.drill,
                ...(view.replaceForStageId ? { replaceForStageId: view.replaceForStageId } : {}),
                ...(view.custom ? { custom: view.custom } : {}),
              })
            }
          />
        )}

        {view.kind === 'pick-par' && (
          <ParPicker
            drill={view.drill}
            mode={view.mode}
            distanceCm={view.distanceCm}
            disciplines={disciplines}
            disciplinesError={disciplinesError}
            onRetryDisciplines={loadDisciplines}
            onConfirm={async ({ disciplineId, ...par }) => {
              if (view.custom) {
                // Custom one-off drill — no catalog drill id; optionally
                // persist a draft for later admin review.
                const result = await api.startCustomRound({
                  sessionId: view.session.id,
                  name: view.custom.name,
                  targetTypeId: view.custom.targetTypeId,
                  shotCount: view.custom.shotCount,
                  magazines: view.custom.magazines,
                  disciplineId,
                  mode: view.mode,
                  distanceCm: view.distanceCm,
                  parSecondsOverride: par.parSecondsOverride,
                  parModeOverride: par.parModeOverride,
                  ...(par.parDisabled ? { parDisabled: true } : {}),
                  saveAsDraft: view.custom.saveAsDraft,
                });
                setView({
                  kind: 'pick-order',
                  session: view.session,
                  stageId: result.stage.id,
                  stageName: result.stage.name,
                });
              } else if (view.replaceForStageId) {
                await api.replacePlannedStage(
                  view.replaceForStageId,
                  view.drill.drill.id,
                  view.mode,
                  view.distanceCm,
                  par,
                  disciplineId,
                );
                // After replace, the slot is still planned — show the
                // picker again so the referee can either Start it now
                // or move on.
                setView({ kind: 'pick-drill', session: view.session });
              } else {
                const result = await api.startDrillRound(
                  view.session.id,
                  view.drill.drill.id,
                  disciplineId,
                  view.mode,
                  view.distanceCm,
                  par,
                );
                setView({
                  kind: 'pick-order',
                  session: view.session,
                  stageId: result.stage.id,
                  stageName: result.stage.name,
                });
              }
            }}
            onBack={() =>
              setView({
                kind: 'pick-distance',
                session: view.session,
                drill: view.drill,
                mode: view.mode,
                ...(view.replaceForStageId ? { replaceForStageId: view.replaceForStageId } : {}),
                ...(view.custom ? { custom: view.custom } : {}),
              })
            }
          />
        )}

        {view.kind === 'pick-planned-discipline' && (
          <PlannedDisciplinePicker
            stage={view.stage}
            disciplines={disciplines}
            disciplinesError={disciplinesError}
            onRetryDisciplines={loadDisciplines}
            onConfirm={async (disciplineId) => {
              const result = await api.startPlannedStage(view.stage.id, disciplineId);
              setView({
                kind: 'pick-order',
                session: view.session,
                stageId: result.stage.id,
                stageName: result.stage.name,
              });
            }}
            onBack={() => setView({ kind: 'pick-drill', session: view.session })}
          />
        )}

        {view.kind === 'pick-order' && (
          <OrderPicker
            stageName={view.stageName}
            onPicked={(shuffle) => startFirstAssignmentOnStage(view.session, view.stageId, shuffle)}
            onBack={() => setView({ kind: 'roster', session: view.session })}
          />
        )}

        {view.kind === 'score' && (
          <ScoreScreen
            key={view.active.assignment.id}
            sessionId={view.session.id}
            assignmentId={view.active.assignment.id}
            roundId={view.active.round.id}
            roundArmedAt={view.active.round.armedAt}
            shotCount={view.active.stage.shotCount}
            magazines={view.active.stage.magazines}
            shooterLabel={shooterLabelOf(view.active)}
            scoringSchemaCode={view.active.scoringSchemaCode}
            scoringSchemaKind={view.active.scoringSchemaKind}
            scoringSchemaConfig={view.active.scoringSchemaConfig}
            scoringSchemaZones={view.active.scoringSchemaZones}
            targetZones={view.active.targetZones}
            penalties={view.active.penalties}
            penaltyValues={view.active.penaltyValues}
            scoringMode={view.active.scoringMode}
            distanceCm={view.active.distanceCm}
            disciplineCode={view.active.disciplineCode}
            maxRecordingSeconds={view.active.maxRecordingSeconds}
            scenarioTitle={
              view.active.scenarioTitleI18n
                ? (view.active.scenarioTitleI18n['de-AT'] ??
                  view.active.scenarioTitleI18n['en'] ??
                  view.active.scenarioCode ??
                  null)
                : view.active.scenarioCode
            }
            effectiveParSeconds={
              view.active.effectiveParSeconds == null
                ? null
                : Number(view.active.effectiveParSeconds)
            }
            effectiveParMode={view.active.effectiveParMode}
            completedAt={view.active.assignment.completedAt}
            initialFirstShotMs={view.active.assignment.firstShotTimeMs}
            initialElapsedMs={view.active.assignment.elapsedTimeMs}
            disqualifiedAt={view.active.assignment.disqualifiedAt}
            dqReason={view.active.assignment.dqReason}
            nextShooterLabel={(() => {
              const n = view.nextUp ?? nextPending(view.roster, view.active);
              return n ? shooterLabelOf(n) : null;
            })()}
            nextShooterNotice={
              view.nextUp
                ? `Die Reihenfolge hat sich geändert — als ${t.style === 'masculine' ? 'Nächster' : 'Nächste:r'} ist jetzt ${shooterLabelOf(view.nextUp)} dran. Zum Aufrufen nochmal tippen.`
                : null
            }
            onCallNext={() => gotoNext(view, view.nextUp ?? nextPending(view.roster, view.active))}
            calledAt={view.active.assignment.calledAt ?? null}
            standbyAt={view.active.assignment.standbyAt ?? null}
            onBack={() => setView({ kind: 'roster', session: view.session })}
            onRoundComplete={() =>
              setView({
                kind: 'round-complete',
                session: view.session,
                stageId: view.active.stage.id,
                stageName: view.active.stage.name,
              })
            }
            onRefetchRoster={async () => {
              const activeId = view.active.assignment.id;
              try {
                const fresh = await api.liveAssignments(view.session.id);
                // Functional: after a slow refetch the referee may already
                // be on another shooter (or screen) — only refresh while
                // still on this shooter's score view, and keep `nextUp`.
                setView((v) => {
                  if (v.kind !== 'score' || v.active.assignment.id !== activeId) return v;
                  const updatedActive = fresh.find((a) => a.assignment.id === activeId) ?? v.active;
                  return { ...v, active: updatedActive, roster: fresh };
                });
                return true;
              } catch (err) {
                console.error('liveAssignments refetch failed', err);
                return false;
              }
            }}
            onOpenTimerDebug={() =>
              setView((v) => (v.kind === 'timer-debug' ? v : { kind: 'timer-debug', from: v }))
            }
            allowPhoneTimer={view.session.signOffState === 'self_signed'}
          />
        )}

        {view.kind === 'round-complete' && (
          <RoundCompleteScreen
            session={view.session}
            stageId={view.stageId}
            stageName={view.stageName}
            onSameDrill={(stageId) =>
              setView({
                kind: 'pick-order',
                session: view.session,
                stageId,
                stageName: view.stageName,
              })
            }
            onDifferentDrill={() => setView({ kind: 'pick-drill', session: view.session })}
            onOverview={() => setView({ kind: 'roster', session: view.session })}
            onBadeschluss={async () => {
              await badeschluss(view.session);
            }}
          />
        )}

        {view.kind === 'timer-debug' && (
          <TimerDebug
            onBack={() => setView(view.from)}
            runFor={view.from.kind === 'score' ? view.from.active.assignment.id : null}
          />
        )}
      </main>

      {badeschlussFor && (
        <BadeschlussSignatures
          session={badeschlussFor}
          onCancel={() => setBadeschlussFor(null)}
          onClosed={() => {
            setBadeschlussFor(null);
            setView({ kind: 'pick-session' });
          }}
        />
      )}
    </div>
  );
}
